'use client'

import NextLink from 'next/link'
import throttle from 'lodash/fp/throttle'
import { useEffect, useState } from 'react'
import slugify from '@sindresorhus/slugify'
import useAnalytics from '@hooks/use-analytics'
import { Icon, NavBar, NavItem } from '@shc/ui'
import { getIconFromContentful } from '@lib/icons'
import { routeMapper } from '@lib/mappers/menu-item-mapper'
import type { TypeNavBarBlockFields } from '@lib/generated-types'
import { doScroll } from '@hooks/useCustomScroll'

/**
 * NavBarBlock is a React functional component that renders a navigation bar
 * with menu items and handles active section highlighting based on scroll position.
 *
 * @param {TypeNavBarBlockFields} props - The props for the NavBarBlock component.
 * @param {TypeNavBarBlockFields['sectionLink']} props.sectionLink - The section link used to generate the ID for the navigation bar.
 * @param {TypeNavBarBlockFields['menuItems']} props.menuItems - An array of menu items to be displayed in the navigation bar.
 * Each menu item contains fields such as `link`, `anchorOrExternalLink`, `icon`, and `title`.
 * @param {TypeNavBarBlockFields['alignment']} props.alignment - The alignment of the navigation bar, either 'left' or 'center'.
 *
 * @returns {JSX.Element} The rendered navigation bar component.
 *
 * @remarks
 * - The component uses `useEffect` to set the active section based on the current URL hash
 *   and to handle scroll events for updating the active section dynamically.
 * - The `throttle` function is used to optimize scroll event handling.
 * - The `routeMapper` function is used to map menu item links to routes.
 * - The `trackSnowplow` function is used to track navigation click events for analytics.
 *
 * @todo Move the alignment map to a separate file for better maintainability.
 * @todo Refactor the scroll handling logic to avoid directly accessing the DOM and improve testability.
 *
 * @example
 * ```tsx
 * <NavBarBlock
 *   sectionLink="example-section"
 *   alignment="center"
 *   menuItems={[
 *     { fields: { link: '/home', title: 'Home', icon: 'home-icon' } },
 *     { fields: { link: '/about', title: 'About', icon: 'about-icon' } },
 *   ]}
 * />
 * ```
 */
const NavBarBlock = ({ sectionLink, menuItems, alignment }: TypeNavBarBlockFields) => {
  const throttleMs = 100
  const subNavBarHeight = 64
  const { trackSnowplow } = useAnalytics()
  const [activeSection, setActiveSection] = useState<string | null>(null)

  const sectionElementIds = menuItems.map(({ fields }) => {
    const { route } = routeMapper(fields.link, fields.anchorOrExternalLink)
    const sectionIndex = route.indexOf('#')

    if (sectionIndex != -1) return route.substring(sectionIndex + 1)

    return null
  })

  const alignmentMap: { left: 'start'; center: 'center' } = {
    left: 'start',
    center: 'center',
  }

  useEffect(() => {
    setActiveSection(typeof window !== 'undefined' ? window.location.hash.slice(1) : null)
  }, [])

  useEffect(() => {
    const handleScroll = throttle(throttleMs, () => {
      for (const sectionElementId of sectionElementIds) {
        if (sectionElementId) {
          const section = document.getElementById(sectionElementId)
          if (
            section &&
            section.getBoundingClientRect().top < window.scrollOffSet + subNavBarHeight
          ) {
            setActiveSection(sectionElementId)
          }
        }
      }
    })

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <NavBar
      id={sectionLink && slugify(sectionLink)}
      alignment={alignmentMap[alignment]}
      sticky
      // className="bg-primary-50 h-12 lg:h-16" // what is this for maaaan?
      data-block="navBar"
    >
      {menuItems.map(({ fields }) => {
        const { route, isInternal } = routeMapper(fields.link, fields.anchorOrExternalLink)
        const icon = getIconFromContentful(fields.icon)
        const active = activeSection === route.split('#')[1]
        const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
          trackSnowplow({
            event: {
              name: 'navigation_click',
              data: {
                navigation_tree: fields.title,
                navigation_level: 1,
                navigation_subject: fields.title,
                navigation_url: e.currentTarget.href,
              },
            },
            contexts: [{ name: 'section', data: { section_name: 'subnav' } }],
          })

          const hasAnchorIndex = String(e.currentTarget).indexOf('#')
          if (hasAnchorIndex !== -1) {
            const [currentPath] = String(window.location.href).split('#')
            const [targetPath, targetAnchor] = String(e.currentTarget).split('#')
            if (currentPath === targetPath) {
              // Scroll
              const element = document.getElementById(targetAnchor)
              if (element) {
                doScroll(element.getBoundingClientRect().top + window.scrollY - window.scrollOffSet)
              }

              window.history.replaceState(null, '', String(e.currentTarget))
              e.preventDefault()
            }
          }
        }
        return (
          <NavItem
            as={NextLink}
            active={active}
            key={fields.title}
            href={route}
            target={isInternal ? undefined : '_blank'}
            rel={isInternal ? undefined : 'noopener noreferrer'}
            onClick={onClick}>
            {fields.icon && <Icon icon={icon} className="pr-1.5" />}
            {fields.title}
          </NavItem>
        )
      })}
    </NavBar>
  )
}

export default NavBarBlock
